import { TenantDto, UpsertTenantCommand } from "api/tenant";
import { useAppSelector } from "app/hooks";
import { useAffectedQuotesConfirm } from "components/domain/AffectedQuotes";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { useRgiRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { FormikProps } from "formik";
import { usePanel } from "hooks/usePanel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { tenantsAPI } from "services/swagger-api";
import { parseApiError } from "shared/utils/apiValidationErrors";
import { buildTenantFormConfig } from "../form/tenantFormConfig";
import { mapTenant } from "../mappers/mapTenant";
import { mapTenantFormValues } from "../mappers/mapTenantFormValues";
import { TenantFormValues } from "../types/tenantFormValues";
import { useTenantFormActions } from "./useTenantFormActions";

export interface UseTenantFormProps {
  riskId?: string;
  tenantId?: string;
  refreshTenants?: () => void;
  /** Bien cible explicite (tunnel parc) — par défaut le bien du risque mono-bien. */
  realEstateId?: string;
}

export const useTenantForm = ({
  riskId,
  tenantId,
  refreshTenants,
  realEstateId,
}: UseTenantFormProps) => {
  const [tenant, setTenant] = useState<TenantDto | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const { handleDelete } = useTenantFormActions({ refreshTenants })
  const riskData = useRgiRiskData();
  const targetRealEstateId = realEstateId ?? riskData?.realEstate?.id
  const contractStartDate = riskData?.contractStartDate
  const referentialsData = useAppSelector(selectReferentialData)

  const { setPanelActions, closePanel } = usePanel();
  const formikRef = useRef<FormikProps<TenantFormValues>>(null);

  const [isFormValid, setIsFormValid] = useState(false)
  const [hasFormChanges, setHasFormChanges] = useState(false)

  const isEditMode = !!tenantId;

  useEffect(() => {
    const fetchTenant = async (tenantId: string) => {
      try {
        setIsLoading(true)
        const result = await tenantsAPI.getTenantById({ tenantId })
        if (result) {
          setTenant(result)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
    if (tenantId)
      fetchTenant(tenantId)
  }, [tenantId])

  const executeSubmit = useCallback(async (): Promise<void> => {
    if (!targetRealEstateId || !riskId)
      return
    try {
      const values = formikRef.current?.values;
      if (!values) return;

      const upsertTenantCommand: UpsertTenantCommand = {
        ...mapTenantFormValues(values, referentialsData),
        realEstateId: targetRealEstateId,
        riskId
      }
      if (tenantId) {
        await tenantsAPI.updateTenant({ tenantId, upsertTenantCommand })
      } else {
        await tenantsAPI.createTenant({ upsertTenantCommand })
      }
      refreshTenants && refreshTenants()
      closePanel()
    } catch (error: any) {
      console.error("Error submitting tenant:", error);
      const parsed = await parseApiError(error);
      toast.error(parsed.message || error?.response?.error?.message || "An error occurred");
    }
  }, [targetRealEstateId, riskId, tenantId, referentialsData, refreshTenants, closePanel]);

  const fetchAffectedQuotes = useCallback(async () => {
    if (!tenantId) return undefined;
    try {
      return await tenantsAPI.getAffectedQuotesByTenantId({ tenantId });
    } catch (error) {
      console.error("Error fetching affected quotes:", error);
      return undefined;
    }
  }, [tenantId]);

  const { showConfirmationDialog } = useAffectedQuotesConfirm({
    entityType: 'tenant',
    fetchAffectedQuotes,
    onConfirm: executeSubmit,
    isEditMode
  });

  const handleSubmitWithConfirmation = useCallback(async () => {
    if (isEditMode && hasFormChanges) {
      // Show confirmation dialog only if there are changes in edit mode
      await showConfirmationDialog();
    } else {
      // No changes or not in edit mode, submit directly
      await executeSubmit();
    }
  }, [isEditMode, hasFormChanges, showConfirmationDialog, executeSubmit]);

  useEffect(() => {
    // In edit mode, disable button if no changes
    const isSubmitDisabled = !isFormValid || (isEditMode && !hasFormChanges);

    const basePanelActions = {
      closeLabel: "COMMON.CANCEL",
      submitLabel: "COMMON.VALIDATE",
      submitDisabled: isSubmitDisabled,
      onSubmit: handleSubmitWithConfirmation,
    };

    if (tenantId) {
      setPanelActions({
        ...basePanelActions,
        onDelete: () => handleDelete(tenantId),
        deleteLabel: "COMMON.DELETE"
      });
    } else {
      setPanelActions(basePanelActions);
    }
  }, [closePanel, setPanelActions, tenantId, handleDelete, isFormValid, isEditMode, hasFormChanges, handleSubmitWithConfirmation]);

  const submitTenant = useCallback(async (values: TenantFormValues): Promise<void> => {
    if (!targetRealEstateId || !riskId)
      return
    try {
      const upsertTenantCommand: UpsertTenantCommand = {
        ...mapTenantFormValues(values, referentialsData),
        realEstateId: targetRealEstateId,
        riskId
      }
      if (tenantId) {
        await tenantsAPI.updateTenant({ tenantId, upsertTenantCommand })
      } else {
        await tenantsAPI.createTenant({ upsertTenantCommand })
      }
      refreshTenants && refreshTenants()
    } catch (error: any) {
      console.error("Error submitting tenant:", error);
      const parsed = await parseApiError(error);
      toast.error(parsed.message || error?.response?.error?.message || "An error occurred");
    }
  }, [targetRealEstateId, riskId, tenantId, referentialsData, refreshTenants]);

  const formConfig = useMemo(() => buildTenantFormConfig(contractStartDate), [contractStartDate]);

  const initialValues = useMemo(() => {
    if (!tenant) {
      return formConfig.initialValues;
    }
    return mapTenant(tenant, referentialsData);
  }, [tenant, referentialsData, formConfig]);

  const validationSchema = useMemo(() => {
    return formConfig.validationSchema;
  }, [formConfig]);

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid)
  }, [])

  const handleFormChange = useCallback((hasChanges: boolean) => {
    setHasFormChanges(hasChanges)
  }, [])

  return {
    initialValues,
    formikRef,
    submitTenant,
    validationSchema: validationSchema,
    handleValidationChange,
    handleFormChange,
    isLoading,
    tenant
  };
};
